import React, { useRef, useState } from "react";
import { X, Download, Loader2 } from "lucide-react";
import { toJpeg } from "html-to-image";

// ─── helpers ─────────────────────────────────────────────────────────────────
const numberToWords = (num) => {
  const a = ["", "One ", "Two ", "Three ", "Four ", "Five ", "Six ", "Seven ", "Eight ", "Nine ", "Ten ", "Eleven ", "Twelve ", "Thirteen ", "Fourteen ", "Fifteen ", "Sixteen ", "Seventeen ", "Eighteen ", "Nineteen "];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  if ((num = num.toString()).length > 9) return "Overflow";
  const n = ("000000000" + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return "";
  let str = "";
  str += n[1] !== "00" ? (a[Number(n[1])] || b[n[1][0]] + " " + a[n[1][1]]) + "Crore " : "";
  str += n[2] !== "00" ? (a[Number(n[2])] || b[n[2][0]] + " " + a[n[2][1]]) + "Lakh " : "";
  str += n[3] !== "00" ? (a[Number(n[3])] || b[n[3][0]] + " " + a[n[3][1]]) + "Thousand " : "";
  str += n[4] !== "0" ? (a[Number(n[4])] || b[n[4][0]] + " " + a[n[4][1]]) + "Hundred " : "";
  str += n[5] !== "00" ? ((str !== "") ? "and " : "") + (a[Number(n[5])] || b[n[5][0]] + " " + a[n[5][1]]) + "Only" : "Only";
  return str;
};

const fmt = (v) => Number(v || 0).toFixed(2);

// ─── Component ───────────────────────────────────────────────────────────────
export default function PayslipModal({ isOpen, onClose, data }) {
  const componentRef = useRef();
  const [isDownloading, setIsDownloading] = useState(false);



  // Download directly as a high-quality JPEG image
  const handleDownloadImage = async () => {
    if (!componentRef.current) return;
    setIsDownloading(true);
    try {
      // html-to-image is much better at capturing SVGs correctly than html2canvas
      const imgData = await toJpeg(componentRef.current, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: "#ffffff"
      });
      const link = document.createElement("a");
      link.download = `Payslip_${data?.full_name?.replace(/\s+/g, "_") || data?.employee_name?.replace(/\s+/g, "_")}_${data?.month}.jpeg`;
      link.href = imgData;
      link.click();
    } catch (err) {
      console.error("Failed to download image", err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen || !data) return null;

  const monthName = data.month || "N/A";
  const year = data.year || "N/A";
  const words = numberToWords(Math.round(Number(data.net_salary || 0)));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[95vh] overflow-hidden">

        {/* Header Controls */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 rounded-t-2xl shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-800">Payslip — {data.full_name || data.employee_name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{monthName} {year} · Net: ₹{fmt(data.net_salary)}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Download as Image Button */}
            <button
              onClick={handleDownloadImage}
              disabled={isDownloading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all"
            >
              {isDownloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              Download
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full text-gray-600 transition ml-2"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Printable HTML Canvas Area */}
        <div className="overflow-y-auto overflow-x-auto flex-1 bg-gray-200 p-2 md:p-8 flex justify-start md:justify-center">
          {/* This is the printable document. A4 size simulation */}
          <div
            ref={componentRef}
            className="bg-white text-black shadow-lg relative overflow-hidden"
            style={{
              width: "100%",
              width: "100%",
              minWidth: "800px",
              minHeight: "297mm",
              padding: "0",
              fontFamily: "'Times New Roman', Times, serif",
              WebkitPrintColorAdjust: "exact",
              printColorAdjust: "exact"
            }}
          >
            {/* Top Shapes (Blue & Cyan Blocks) */}
            <div className="absolute top-0 left-0">
              <div className="w-[60px] h-[50px] bg-blue-600"></div>
              <div className="w-[60px] h-[50px] bg-[#38bdf8]  ml-[60px]"></div>
            </div>


            {/* Logo Area (Right Aligned) */}
            <div className="flex justify-end pt-12 pr-12 relative z-10">
              <div className="flex items-center gap-2">
                <img src="/visiontreklogo.svg" alt="Logo" className="h-16 object-contain" />
              </div>
            </div>

            {/* Content Container */}
            <div className="pt-20 px-12 md:px-16">

              {/* GST */}
              <div className="mb-8">
                <p className="font-bold text-[13px] md:text-[15px]">GST No. <span className="font-normal">06AATFV3923G1ZU</span></p>
              </div>

              {/* Title */}
              <div className="text-center mb-10">
                <h2 className="text-[16px] md:text-[18px] font-bold underline decoration-2 underline-offset-4 inline-block">Salary Slip</h2>
              </div>

              {/* Employee Info */}
              <div className="mb-8 space-y-1 text-[13px] md:text-[15px] font-bold">
                <div className="grid grid-cols-[160px_1fr]">
                  <span>Employee Name:</span>
                  <span className="font-normal">{data.full_name || data.employee_name}</span>
                </div>
                <div className="grid grid-cols-[160px_1fr]">
                  <span>Employee ID:</span>
                  <span className="font-normal">VT-{String(data.user_id).padStart(3, '0')}</span>
                </div>
                <div className="grid grid-cols-[160px_1fr]">
                  <span>Employee Email:</span>
                  <span className="font-normal underline text-blue-600">{data.email}</span>
                </div>
                <div className="grid grid-cols-[160px_1fr]">
                  <span>Designation:</span>
                  <span className="font-normal text-capitalize">{data.designation || "Employee"}</span>
                </div>
                <div className="grid grid-cols-[160px_1fr]">
                  <span>Month:</span>
                  <span className="font-normal">{monthName}</span>
                </div>
                <div className="grid grid-cols-[160px_1fr]">
                  <span>Year:</span>
                  <span className="font-normal">{year}</span>
                </div>
              </div>

              {/* Table */}
              <div className="mb-6">
                <table className="w-full border-collapse border border-black text-[12px] md:text-[13px]">
                  <thead>
                    <tr>
                      <th className="border border-black text-left p-1.5 w-1/4 font-bold">Earnings</th>
                      <th className="border border-black text-left p-1.5 w-1/4"></th>
                      <th className="border border-black text-left p-1.5 w-1/4 font-bold">Deductions</th>
                      <th className="border border-black text-left p-1.5 w-1/4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-black p-1.5">Basic & DA</td>
                      <td className="border border-black p-1.5 text-right">{fmt(data.monthly_salary || data.salary)}</td>
                      <td className="border border-black p-1.5">Provident Fund</td>
                      <td className="border border-black p-1.5 text-right">{fmt(data.pf_deduction)}</td>
                    </tr>
                    <tr>
                      <td className="border border-black p-1.5">HRA</td>
                      <td className="border border-black p-1.5 text-right">{fmt(data.hra || data.earnings)}</td>
                      <td className="border border-black p-1.5">E.S.I.</td>
                      <td className="border border-black p-1.5 text-right">{fmt(data.esi_deduction || data.esi)}</td>
                    </tr>
                    <tr>
                      <td className="border border-black p-1.5">Personal Pay</td>
                      <td className="border border-black p-1.5 text-right">{fmt(data.special_allowance || data.personal_pay)}</td>
                      <td className="border border-black p-1.5">Loan/Misc.</td>
                      <td className="border border-black p-1.5 text-right">{fmt(data.misc_deduction)}</td>
                    </tr>
                    <tr>
                      <td className="border border-black p-1.5"></td>
                      <td className="border border-black p-1.5 text-right"></td>
                      <td className="border border-black p-1.5">Leave Without Pay [LWP]</td>
                      <td className="border border-black p-1.5 text-right">{fmt(data.leave_deduction || data.lwop)}</td>
                    </tr>
                    <tr>
                      <td className="border border-black p-1.5 font-bold">Total Addition</td>
                      <td className="border border-black p-1.5 text-right">{fmt(data.gross_salary || (Number(data.monthly_salary || data.salary) + Number(data.hra || data.earnings || 0) + Number(data.special_allowance || data.personal_pay || 0)))}</td>
                      <td className="border border-black p-1.5 font-bold">Total Deduction</td>
                      <td className="border border-black p-1.5 text-right">{fmt(data.total_deduction || (Number(data.pf_deduction || 0) + Number(data.esi_deduction || data.esi || 0) + Number(data.misc_deduction || 0) + Number(data.leave_deduction || data.lwop || 0)))}</td>
                    </tr>
                    <tr>
                      <td className="border border-black p-1.5"></td>
                      <td className="border border-black p-1.5 text-right"></td>
                      <td className="border border-black p-1.5 font-bold">NET Salary</td>
                      <td className="border border-black p-1.5 text-right font-bold">{fmt(data.net_salary)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Rupees in Words */}
              <div className="mb-10 mt-[-16px] text-[13px] md:text-[15px] font-bold">
                <span>Rupees: </span>
                <span className="font-normal">{words}</span>
              </div>



              {/* <div className=" flex justify-start items-center mt-[-24px]">
                <span className="font-bold">Cheque No: </span>
                <span className="font-normal">{data.cheque_no || "Bank Transfer"}</span>
              </div> */}


              {/* Bank & Footer Details */}
              <div className="grid grid-cols-[1fr_200px] mb-20 text-[13px] md:text-[15px] mt-[-24px]">
                <div className="space-y-0">

                  <div className=" grid grid-cols-[100px_1fr] font-bold">
                    <span>Cheque No: </span>
                    <span className="font-normal">{data.cheque_no || "Bank Transfer"}</span>
                  </div>
                  <div className="grid grid-cols-[100px_1fr] font-bold ">
                    <span>Account No:</span>
                    <span className="font-normal">{data.account_number}</span>
                  </div>
                  <div className="grid grid-cols-[100px_1fr] font-bold">
                    <span>Bank Name:</span>
                    <span className="font-normal">{data.bank_name}</span>
                  </div>
                  <div className="grid grid-cols-[100px_1fr] font-bold">
                    <span>PAN No:</span>
                    <span className="font-normal">{data.pan_number}</span>
                  </div>
                  <div className="grid grid-cols-[100px_1fr] font-bold mt-6 ">
                    <span>Date:</span>
                    <span className="font-normal">{new Date().toLocaleDateString("en-GB")}</span>
                  </div>


                </div>


              </div>
              <div className="grid grid-cols-[1fr_200px] mb-20 text-[13px] md:text-[15px] mt-[-24px]">
                <div className="space-y-0">

                  <div className=" flex justify-start items-start mt-[-34px] gap-2">
                    <span className="font-semibold">Note:</span>
                    <span className="font-normal">This Payslip is Auto Generated.</span>
                  </div>
                </div>
              </div>





              {/* Signatures */}
              <div className="mb-24 text-[13px] md:text-[15px] mt-[-64px]">
                <p>HR Department</p>
                <p>Visiontrek Communication</p>
              </div>
            </div>



            {/* Bottom Shapes (Blue & Cyan Blocks) */}
            <div className="absolute bottom-0 left-0">
              <div className="w-[60px] h-[50px] bg-blue-600 mt-[-80px]"></div>
              <div className="w-[60px] h-[50px] bg-[#38bdf8]  ml-[60px]"></div>
            </div>

            {/* Bottom Contact Footer */}
            <div className="absolute bottom-12 left-44 right-32 flex justify-between items-center" style={{ fontFamily: "Arial" }}>
              <div className="space-y-1 text-[10px] md:text-[11px] text-[#4b5563]">
                <p className="flex items-center gap-2"><span className="text-[#0ea5e9] font-bold">📞</span> +91 93509 09124</p>
                <p className="flex items-center gap-2"><span className="text-[#0ea5e9] font-bold">✉️</span> info@visiontrek.in</p>
                <p className="flex items-center gap-2"><span className="text-[#0ea5e9] font-bold">🌐</span> www.visiontrek.in</p>
              </div>
              <div className="border-l-2 border-[#bae6fd] pl-4 text-[10px] md:text-[11px] text-[#4b5563]">
                <p className="font-bold text-[#1e3a8a] mb-1 flex items-center gap-2 text-[11px] md:text-[12px]">
                  <span className="text-[#0ea5e9]">📍</span> VISIONTREK COMMUNICATION
                </p>
                <p className="ml-5">E-195, Industrial Area,</p>
                <p className="ml-5">Phase 8B, Mohali, Punjab - India</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
